import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { catchError, finalize } from 'rxjs/operators';
import {
  Denounce,
  DenounceListResponse,
  DenounceFilters,
  DenounceAccessCheck,
  DenounceOperator,
} from '../interfaces/denounce.interface';

@Injectable({
  providedIn: 'root',
})
export class DenounceAdminService {
  private apiUrl = `${environment.apiNest}denounces`;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  private handleError(error: HttpErrorResponse) {
    let customError: any = {
      statusCode: error.status,
      message: 'Ha ocurrido un error en el servicio',
      ...error.error,
    };
    if (error.error instanceof ErrorEvent) {
      customError.message = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 0:
          customError.message = 'No se pudo conectar al servidor.';
          break;
        case 401:
          customError.message = 'Error de autenticación.';
          break;
        case 403:
          customError.message = 'No tiene permisos para esta acción.';
          break;
        case 404:
          customError.message = 'Recurso no encontrado.';
          break;
        default:
          customError.message = error.error?.message || `Error: ${error.status}`;
      }
    }
    return throwError(() => customError);
  }

  private buildParams(filters: DenounceFilters): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  // --- Access ---

  checkAccess(): Observable<DenounceAccessCheck> {
    return this.http.get<DenounceAccessCheck>(`${this.apiUrl}/access/check`).pipe(
      catchError(this.handleError),
    );
  }

  // --- Admin: Denuncias ---

  getList(filters: DenounceFilters): Observable<DenounceListResponse> {
    this.loadingSubject.next(true);
    const params = this.buildParams(filters);
    return this.http.get<DenounceListResponse>(`${this.apiUrl}/list`, { params }).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  getDetail(id: string): Observable<Denounce> {
    this.loadingSubject.next(true);
    return this.http.get<Denounce>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  updateStatus(
    id: string,
    status: string,
    notes?: string,
    finalMessage?: string,
    finalMessageAttachmentKeys?: string[],
  ): Observable<any> {
    this.loadingSubject.next(true);
    const body: any = { status };
    if (notes) body.notes = notes;
    if (finalMessage) body.finalMessage = finalMessage;
    if (finalMessageAttachmentKeys?.length) {
      body.finalMessageAttachmentKeys = finalMessageAttachmentKeys;
    }
    return this.http.put<any>(`${this.apiUrl}/${id}/status`, body).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  assign(id: string, operatorId: string | null): Observable<any> {
    this.loadingSubject.next(true);
    return this.http.put<any>(`${this.apiUrl}/${id}/assign`, { operatorId }).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  getMessages(id: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${id}/messages`).pipe(
      catchError(this.handleError),
    );
  }

  sendMessage(id: string, content: string, attachmentKeys?: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/messages`, { content, attachmentKeys }).pipe(
      catchError(this.handleError),
    );
  }

  uploadFile(fileBase64: string, fileName: string): Observable<{
    storageKey: string; fileName: string; fileSize: number; fileType: string;
  }> {
    return this.http.post<any>(`${this.apiUrl}/upload`, { fileBase64, fileName }).pipe(
      catchError(this.handleError),
    );
  }

  getAttachmentUrl(denounceId: string, attachmentId: string): Observable<{
    url: string; fileName: string; fileType: string;
  }> {
    return this.http
      .get<any>(`${this.apiUrl}/${denounceId}/attachment/${attachmentId}/url`)
      .pipe(catchError(this.handleError));
  }

  exportCsv(filters: DenounceFilters): Observable<any> {
    this.loadingSubject.next(true);
    const params = this.buildParams(filters);
    return this.http.get<any>(`${this.apiUrl}/export`, { params }).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  // --- Métricas / Dashboard home ---

  getMetrics(dateFrom?: string, dateTo?: string): Observable<{
    range: { dateFrom: string; dateTo: string };
    total: number;
    totalsByStatus: { label: string; count: number }[];
    totalsByCategory: { label: string; count: number }[];
    totalsByRelationship: { label: string; count: number }[];
    anonymous: { anonymous: number; identified: number; percentAnonymous: number };
    assignmentLoad: {
      operatorId: string | null;
      operatorName: string;
      total: number;
      byStatus: Record<string, number>;
    }[];
    dailySeries: { day: string; count: number }[];
    unassignedOldCount: number;
    avgHoursToInvestigation: number | null;
    avgHoursToClosure: number | null;
  }> {
    let params = new HttpParams();
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);
    return this.http
      .get<any>(`${this.apiUrl}/metrics`, { params })
      .pipe(catchError(this.handleError));
  }
}
